import { defineStore } from 'pinia'
import type { RouteRecordRaw as RouteConfig } from 'vue-router'
/**
 * 此处持久化使用加密loca.
 * 1.locaStorage 确保页面刷新数据不丢失
 * 2.加密确保数据安全性
 */
import { EncryptLocalCache } from '@/utils/encrypt'

const cache = new EncryptLocalCache()

// 定义状态的接口
interface SettingState {
    // 要显示的tab列表
    tabList: RouteConfig[]
    // 选中的tab
    activeTab: RouteConfig | null
    /**
     * 固定的 tab 列表（与 fh-scroll-tab 的 `pinnedKeys` 对齐，可多项）。
     */
    fixedTabs: RouteConfig[]
}

// 设置信息存储
const useRouterTabStore = defineStore('routerTab', {
    state: (): SettingState => ({
        tabList: [],
        activeTab: null,
        fixedTabs: []
    }),
    // 持久化配置（当需要存储的多个持久性时，key必须。否则持久化内容会覆盖）
    persist: {
        storage: cache
    },
    actions: {
        /**
         * 修改当前state的值
         * @param {string} key 缓存key
         * @param {string} value 缓存value
         * @returns {void}
         */
        setStore<T>({ key, value }: { key: string; value: T }) {
            if (key && (value !== null || value !== undefined)) {
                ;(this as any)[key] = value
            }
            return null
        },

        /**
         * 设置选中的tab
         * @param tab 选中的tab
         */
        setActiveTab(tab: RouteConfig | null) {
            this.activeTab = tab
        },

        /**
         * 新增tab列表
         * @param tab
         */
        addTabList(tab: RouteConfig) {
            if (!this.tabList.some(t => t.name === tab.name)) {
                this.tabList.push(tab)
            }
        },

        /**
         * 移除tab列表
         * @param tab
         */
        removeTabList(tab: RouteConfig) {
            this.tabList = this.tabList.filter(t => t.name !== tab.name)
        },

        /**
         * 根据路由自动更新tab
         * @param route 当前路由
         */
        autoUpdateTabFromRoute(route: RouteConfig) {
            // 添加到tab列表（如果不存在）
            if (!this.tabList.some(t => t.name === route.name)) {
                this.addTabList(route)
            }

            // 设置为激活tab
            if (this.activeTab?.name !== route.name) {
                this.setActiveTab(route)
            }
        },

        /**
         * 清除非法的tab（路由不存在）
         * @param validRouteNames 有效的路由名称列表
         */
        cleanupInvalidTabs(validRouteNames: string[]) {
            this.tabList = this.tabList.filter(
                tab => tab.name && typeof tab.name === 'string' && validRouteNames.includes(tab.name)
            )

            // 如果当前激活的tab被移除，则选择第一个tab
            if (this.activeTab && this.activeTab.name && !validRouteNames.includes(String(this.activeTab.name))) {
                this.activeTab = this.tabList.length > 0 ? this.tabList[0] : null
            }

            // 如果固定项被移除，则同步清理固定列表
            this.fixedTabs = this.fixedTabs.filter(
                tab => tab.name && typeof tab.name === 'string' && validRouteNames.includes(tab.name)
            )
        },
        /**
         * 清除所有持久化的数据
         */
        initStore() {
            console.log('清除所有持久化的数据')
            this.tabList = []
            this.activeTab = null
            this.fixedTabs = []
            cache.removeItem('routerTab')
        }
    },
    getters: {
        /**
         * 获取当前state的值
         * @param {string} key 缓存key
         * @return {string} 缓存value
         */
        getStore: (state: SettingState) => (key: string) => {
            if (key) {
                return (state as any)[key]
            }
            return null
        }
    }
})

export default useRouterTabStore
